import { useState, useEffect, useCallback, useRef } from 'react'
import { Coins, ChevronDown, Printer, RefreshCw, X, Check, Undo2 } from 'lucide-react'
import { downloadPdf } from '../../utils/pdfUtils'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import leiloesService from '../../services/leiloesService'
import clientesService from '../../services/clientesService'
import toast from 'react-hot-toast'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—')

function ClienteAutocomplete({ orgId, value, onChange }) {
  const [query, setQuery] = useState(value?.nome || '')
  const [sugestoes, setSugestoes] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!value) setQuery('')
    else setQuery(value.nome)
  }, [value])

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleChange = (e) => {
    const v = e.target.value
    setQuery(v)
    onChange(null)
    if (!v.trim()) { setSugestoes([]); setOpen(false); return }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      if (!orgId || v.trim().length < 2) return
      setLoading(true)
      try {
        const res = await clientesService.listarClientes(orgId, 1, 15, v.trim())
        setSugestoes(res.data || [])
        setOpen(true)
      } catch { setSugestoes([]) }
      finally { setLoading(false) }
    }, 300)
  }

  const selecionar = (c) => {
    setQuery(c.nome)
    setSugestoes([])
    setOpen(false)
    onChange({ id: c.id, nome: c.nome })
  }

  const limpar = () => {
    setQuery('')
    setSugestoes([])
    setOpen(false)
    onChange(null)
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center border border-gray-300 rounded-lg bg-white overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => sugestoes.length > 0 && setOpen(true)}
          placeholder="Buscar cliente..."
          className="flex-1 px-3 py-2 text-sm outline-none bg-transparent min-w-[180px]"
        />
        {loading && <RefreshCw size={14} className="text-gray-400 animate-spin mr-2 flex-shrink-0" />}
        {query && !loading && (
          <button onClick={limpar} className="p-1.5 mr-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X size={14} />
          </button>
        )}
      </div>
      {open && sugestoes.length > 0 && (
        <div className="absolute z-50 top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {sugestoes.map(c => (
            <button key={c.id} onMouseDown={() => selecionar(c)}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 truncate">
              {c.nome}
            </button>
          ))}
        </div>
      )}
      {open && !loading && sugestoes.length === 0 && query.trim().length >= 2 && (
        <div className="absolute z-50 top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-400">
          Nenhum cliente encontrado
        </div>
      )}
    </div>
  )
}

function ModalMarcarCredito({ credito, onClose, onConfirm }) {
  const jaUtilizado = credito.credito_utilizado
  const [dataUso, setDataUso] = useState(credito.credito_utilizado_em || new Date().toISOString().split('T')[0])
  const [observacao, setObservacao] = useState(credito.credito_observacao || '')
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    await onConfirm({ utilizado: true, data_uso: dataUso, observacao: observacao.trim() || null })
    setLoading(false)
  }

  const handleDesmarcar = async () => {
    setLoading(true)
    await onConfirm({ utilizado: false })
    setLoading(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-800">
            {jaUtilizado ? 'Crédito utilizado' : 'Marcar crédito como utilizado'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600 space-y-1">
            <p><span className="font-medium">Cliente:</span> {credito.cliente_nome || '—'}</p>
            <p><span className="font-medium">Descrição:</span> {credito.descricao}</p>
            <p><span className="font-medium">Crédito:</span> <span className="font-semibold text-amber-700">{fmt(credito.valor_credito)}</span></p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data de utilização</label>
            <input
              type="date" value={dataUso} onChange={e => setDataUso(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Observação (opcional)</label>
            <textarea
              value={observacao} onChange={e => setObservacao(e.target.value)} rows={2}
              placeholder="Ex.: abatido na conta X do leilão Y"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-100">
          {jaUtilizado ? (
            <button onClick={handleDesmarcar} disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50">
              <Undo2 size={15} /> Desmarcar
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button onClick={handleConfirm} disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg disabled:opacity-50">
              <Check size={15} /> {jaUtilizado ? 'Salvar' : 'Confirmar uso'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function CreditosClientes() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const iframeRef = useRef(null)

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [statusFiltro, setStatusFiltro] = useState('') // '' | 'nao_utilizado' | 'utilizado'
  const [clienteSelecionado, setClienteSelecionado] = useState(null)
  const [creditos, setCreditos] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [creditoSelecionado, setCreditoSelecionado] = useState(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => setLeiloes([...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))))
      .catch(() => {})
      .finally(() => setLoadingL(false))
  }, [orgId])

  const buildFiltros = useCallback(() => {
    const filtros = {}
    if (leilaoId) filtros.leilao_id = leilaoId
    if (dataInicio) filtros.data_inicio = dataInicio
    if (dataFim) filtros.data_fim = dataFim
    if (statusFiltro === 'utilizado') filtros.utilizado = true
    if (statusFiltro === 'nao_utilizado') filtros.utilizado = false
    if (clienteSelecionado?.id) filtros.cliente_id = clienteSelecionado.id
    return filtros
  }, [leilaoId, dataInicio, dataFim, statusFiltro, clienteSelecionado])

  const carregar = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const res = await gestaoService.listarCreditosCliente(orgId, buildFiltros())
      setCreditos(res.data || [])
    } catch {
      toast.error('Erro ao carregar créditos')
    } finally {
      setLoading(false)
    }
  }, [orgId, buildFiltros])

  useEffect(() => { if (orgId) carregar() }, [orgId, carregar])

  const handleImprimir = async () => {
    if (!orgId) return
    setLoadingPdf(true)
    try {
      const res = await gestaoService.relatorioCreditosClientePdf(orgId, buildFiltros())
      const blob = new Blob([res.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      setPdfUrl(url)
      setShowPdfModal(true)
    } catch {
      toast.error('Erro ao gerar PDF')
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdfModal = () => { setShowPdfModal(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const handleConfirmarMarcacao = async (dados) => {
    try {
      await gestaoService.marcarCreditoUtilizado(orgId, creditoSelecionado.id, dados)
      toast.success(dados.utilizado ? 'Crédito marcado como utilizado' : 'Crédito desmarcado')
      setCreditoSelecionado(null)
      carregar()
    } catch {
      toast.error('Erro ao atualizar crédito')
    }
  }

  const totalGeral = creditos.reduce((s, c) => s + Number(c.valor_credito || 0), 0)
  const naoUtilizados = creditos.filter(c => !c.credito_utilizado)
  const utilizados = creditos.filter(c => c.credito_utilizado)
  const totalNaoUtilizado = naoUtilizados.reduce((s, c) => s + Number(c.valor_credito || 0), 0)
  const totalUtilizado = utilizados.reduce((s, c) => s + Number(c.valor_credito || 0), 0)

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para ver os créditos de clientes.
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Coins className="w-5 h-5 text-amber-600" />
        <h1 className="text-lg font-semibold text-gray-800">Créditos de Clientes</h1>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="text-xs text-gray-500 font-medium">Leilão</label>
            <div className="relative">
              <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)} disabled={loadingL}
                className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                <option value="">Todos os leilões</option>
                {leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Data baixa (de)</label>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Data baixa (até)</label>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Status do crédito</label>
            <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Todos</option>
              <option value="nao_utilizado">Não utilizado</option>
              <option value="utilizado">Utilizado</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Cliente</label>
            <ClienteAutocomplete orgId={orgId} value={clienteSelecionado} onChange={setClienteSelecionado} />
          </div>

          <button onClick={carregar}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
            Filtrar
          </button>

          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!creditos.length || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir / PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Resumo */}
      {creditos.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-xs text-blue-600 font-medium">Total em Crédito</p>
            <p className="text-lg font-bold text-blue-800">{fmt(totalGeral)}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <p className="text-xs text-yellow-600 font-medium">Não Utilizado ({naoUtilizados.length})</p>
            <p className="text-lg font-bold text-yellow-800">{fmt(totalNaoUtilizado)}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <p className="text-xs text-green-600 font-medium">Utilizado ({utilizados.length})</p>
            <p className="text-lg font-bold text-green-800">{fmt(totalUtilizado)}</p>
          </div>
        </div>
      )}

      {/* Tabela */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
        </div>
      ) : creditos.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Nenhum crédito de cliente encontrado com os filtros selecionados
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600">Data Baixa</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600">Cliente</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600">Leilão</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600">Descrição</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 text-right">Crédito</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600">Status</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {creditos.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-xs text-gray-700">{fmtDate(c.data_baixa)}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-700">{c.cliente_nome || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-700">{c.leilao_nome || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-700 max-w-[280px] truncate" title={c.descricao}>{c.descricao}</td>
                  <td className="px-4 py-2.5 text-xs font-semibold text-amber-700 text-right">{fmt(c.valor_credito)}</td>
                  <td className="px-4 py-2.5">
                    {c.credito_utilizado ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Utilizado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                        Não utilizado
                      </span>
                    )}
                    {c.credito_utilizado && c.credito_utilizado_em && (
                      <div className="text-[11px] text-gray-400 mt-0.5">em {fmtDate(c.credito_utilizado_em)}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <button onClick={() => setCreditoSelecionado(c)}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800">
                      {c.credito_utilizado ? 'Ver / editar' : 'Marcar utilizado'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creditoSelecionado && (
        <ModalMarcarCredito
          credito={creditoSelecionado}
          onClose={() => setCreditoSelecionado(null)}
          onConfirm={handleConfirmarMarcacao}
        />
      )}

      {/* Modal PDF */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Relatório de Créditos de Cliente</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, 'Relatorio_Creditos_Cliente.pdf')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Salvar PDF
                </button>
                <button onClick={printPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button onClick={closePdfModal}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Relatório Créditos de Cliente"
                style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
